import { type ReactNode } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { AuthProvider, useAuth, completeMagicLinkIfPresent } from "./auth";
import SignIn from "./pages/SignIn";
import Home from "./pages/Home";
import Landing from "./pages/Landing";
import Wizard from "./pages/Wizard";
import KitPage from "./pages/KitPage";
import Pricing from "./pages/Pricing";
import Usage from "./pages/Usage";
import { PrivacyPage, TermsPage } from "./pages/Legal";
import "./styles.css";

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="shell">
      <header className="topbar">
        <Link to="/app" className="brand">VocabKit</Link>
        <nav><Link to="/new">New kit</Link>{" · "}<Link to="/pricing">Plans</Link>{" · "}<Link to="/usage">Usage</Link></nav>
      </header>
      <main>{children}</main>
    </div>
  );
}

function Protected({ children }: { children: React.ReactElement }) {
  const { loading, uid } = useAuth();
  if (loading) return <p className="muted">Loading…</p>;
  if (!uid) return <Navigate to="/signin" replace />;
  return <Shell>{children}</Shell>;
}

// Signed-in parents never see the marketing page; query params (e.g. the
// Stripe ?checkout=success handoff) ride along to the app.
function RootOrApp() {
  const { loading, uid } = useAuth();
  if (loading) return <p className="muted">Loading…</p>;
  if (uid) {
    const q = window.location.search;
    return <Navigate to={"/app" + (q || "")} replace />;
  }
  return <Landing />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<RootOrApp />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/app" element={<Protected><Home /></Protected>} />
          <Route path="/new" element={<Protected><Wizard /></Protected>} />
          <Route path="/kit/:id" element={<Protected><KitPage /></Protected>} />
          <Route path="/pricing" element={<Protected><Pricing /></Protected>} />
          <Route path="/usage" element={<Protected><Usage /></Protected>} />
          <Route path="/legal/privacy" element={<PrivacyPage />} />
          <Route path="/legal/terms" element={<TermsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

// Magic-link completion runs before render so the auth state settles once.
completeMagicLinkIfPresent().finally(() => {
  ReactDOMCreateRoot();
});

function ReactDOMCreateRoot() {
  // deferred import keeps this file free of a circular react-dom import in tests
  import("react-dom/client").then(({ createRoot }) =>
    createRoot(document.getElementById("root")!).render(<App />),
  );
}
